import { ApolloLink } from '@apollo/client'
import * as msal from '@azure/msal-browser'
import { setContext } from '@apollo/client/link/context'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { storageService } from '../storage/storage-service'

let msalInstance: msal.IPublicClientApplication
let authRequestOptions = {
  scopes: ['email'],
}

if (process.env.REACT_APP_AUTH_PROVIDER === 'auth0') {
  msalInstance =
    await msal.PublicClientApplication.createPublicClientApplication({
      auth: {
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
        authority: `https://${process.env.REACT_APP_AUTH0_AUTHORITY ?? ''}`,
        knownAuthorities: [process.env.REACT_APP_AUTH0_AUTHORITY ?? ''],
        redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URL,
        protocolMode: msal.ProtocolMode.OIDC,
        OIDCOptions: {
          serverResponseType: msal.ServerResponseType.QUERY,
          defaultScopes: ['openid', 'profile'],
        },
      },
      cache: {
        cacheLocation: msal.BrowserCacheLocation.LocalStorage,
      },
      // system: {
      //   loggerOptions: {
      //     loggerCallback(_logLevel: msal.LogLevel, message: string) {
      //       console.log(message)
      //     },
      //     logLevel: msal.LogLevel.Trace,
      //     piiLoggingEnabled: false,
      //   },
      // },
    })
} else {
  msalInstance =
    await msal.PublicClientApplication.createPublicClientApplication({
      auth: {
        clientId: process.env.REACT_APP_AZURE_AD_CLIENT_ID ?? '',
        authority: `https://login.microsoftonline.com/${
          process.env.REACT_APP_AZURE_AD_TENANT_ID ?? ''
        }`,
        redirectUri: process.env.REACT_APP_AZURE_AD_REDIRECT_URL,
      },
      cache: {
        cacheLocation: msal.BrowserCacheLocation.LocalStorage,
      },
    })

  authRequestOptions = { scopes: ['User.Read'] }
}

async function getToken(): Promise<string | null> {
  const testUser = storageService.get<string>('test-user')
  if (testUser) {
    return testUser
  }

  const accounts = msalInstance.getAllAccounts()

  let account: msal.AccountInfo | undefined

  if (process.env.REACT_APP_AUTH_PROVIDER === 'auth0') {
    account = accounts.find(
      (a) =>
        a.idTokenClaims?.aud !== undefined &&
        a.idTokenClaims.aud === (process.env.REACT_APP_AUTH0_CLIENT_ID ?? ''),
    )
  } else
    account = accounts.find(
      (a) => a.tenantId === process.env.REACT_APP_AZURE_AD_TENANT_ID,
    )

  if (!account) {
    return null
  }

  const options = { ...authRequestOptions, account }

  try {
    // @see https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206
    const idTokenClaims = account.idTokenClaims as { exp: number }
    const forceRefresh = new Date(idTokenClaims.exp * 1000) < new Date()

    return (await msalInstance.acquireTokenSilent({ ...options, forceRefresh }))
      .idToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        return (await msalInstance.acquireTokenPopup(options)).idToken
      } catch {
        return null
      }
    }

    return null
  }
}

export async function isLoggedIn(): Promise<boolean> {
  return (await getToken()) !== null
}

export async function login(): Promise<void> {
  await msalInstance.loginPopup({
    ...authRequestOptions,
    redirectUri: `${
      process.env.REACT_APP_AZURE_AD_REDIRECT_URL ?? ''
    }/blank.html`,
  })
}

type OperationContext = {
  token: string
}

const withToken = setContext(async (): Promise<OperationContext> => {
  const token = await getToken()
  if (token) {
    return { token }
  }

  // SSO session has expired, reload the app.
  window.location.reload()

  return { token: '' }
})

const setTokenHeader = new ApolloLink((operation, forward) => {
  const { token } = operation.getContext() as OperationContext

  operation.setContext({
    headers: {
      Authorization: `Bearer ${token}`,
      'Apollo-Require-Preflight': 'true',
    },
  })

  return forward(operation)
})

export const authenticationMiddleware = withToken.concat(setTokenHeader)
